import React, { useState, useRef } from 'react'
import useSize from 'hooks/useSize'
import { scaleLog, scaleLinear } from 'd3-scale'
import { line, curveBasis } from 'd3-shape'
import { YTicks, XTicks } from './svgSandbox'

import './styles.scss'
import './globals.scss'
import * as config from './config'

const f = new Intl.NumberFormat('de-CH')

type CompareChartProps = {
  data: CompCountryDataElement[]
  valueType?: valueTypeType
  dataType?: dataTypeType
  popType?: popTypeType
  selected: [string, string]
  manualSelect?: (code: string) => void
}
export const CompareChart: React.FC<CompareChartProps> = ({
  data,
  valueType = 'new',
  dataType = 'cases',
  popType = 'abs',
  selected,
  manualSelect
}) => {
  const yFunction = (d: any) => {
    if (d[0] < yAxisScale.domain()[0]) return yAxisScale(yAxisScale.domain()[0])
    return yAxisScale(d[0])
  }
  const xFunction = (d: any) => xAxisScale(d[1])
  const [container, size] = useSize()
  const [hlCountries, setHlCountries] = useState<string[]>([])
  const [ttX, setTtX] = useState<number>(config.isMobile ? 0 : 80)
  const [ttOne, setTtOne] = useState<{ y: number; v: number; d: string }>()
  const [ttTwo, setTtTwo] = useState<{ y: number; v: number; d: string }>()

  const svg = useRef<SVGSVGElement>()
  //
  const width = size.width
  const height = size.isMobile ? 350 : 550
  const gutterTop = 2
  const gutterRight = 2
  const gutterLeft = size.isMobile ? 1 : 80
  const gutterBottom = 14
  const plotArea = {
    top: gutterTop,
    right: width - gutterRight,
    left: gutterLeft,
    bottom: height - gutterBottom
  }
  let xTicks = config.xTicks[valueType][dataType][popType]
  let yTicks = config.yTicks[valueType][dataType][popType]
  let drawProp = config.drawProp[valueType][dataType][popType]

  const xAxisScale = React.useMemo(
    () =>
      scaleLinear()
        .domain(config.xDomain[valueType][dataType][popType])
        .range([plotArea.left, plotArea.right]),
    [size, valueType, dataType, popType]
  )

  const yAxisScale = React.useMemo(
    () =>
      scaleLog()
        .domain(config.yDomain[valueType][dataType][popType])
        .range([plotArea.bottom, plotArea.top]),
    [size, valueType, dataType, popType]
  )

  const lineFunction = React.useMemo(
    () =>
      line()
        .x(xFunction)
        .y(yFunction)
        .curve(curveBasis),
    [size, valueType, dataType, popType]
  )
  const findInCountries = (code: string) => {
    return data.find(f => f.code === code)
  }

  let dataC1: CompCountryDataElement | undefined = findInCountries(selected[0])
  let dataC2: CompCountryDataElement | undefined = findInCountries(selected[1])

  let handleMouseMove = e => {
    const parseDate = (dateString: string) => {
        let parts = dateString.split('.')
        return new Date(+parts[2], +parts[1] - 1, +parts[0])
      },
      createDateString = (d: Date, days: number) => {
        return new Date(
          d.setDate(d.getDate() + days)
        ).toLocaleDateString('de-DE', { day: 'numeric', month: 'long' })
      },
      getPos = (val: number, move: number, isFirst) => {
        let pos = yAxisScale(val)
        if (pos < 50) {
          pos = 20
          move = 0
        }
        if (pos > height - 50) {
          pos = height - (isFirst ? 100 : 50)
          move = 0
        }
        return pos + move
      }
    let valC1: dataElement = { v: 0, d: 0 },
      valC2: dataElement = { v: 0, d: 0 },
      move1: number = 0,
      move2: number = 0,
      isFirst: boolean = true,
      tt1_ = { y: 0, v: 0, d: '' },
      tt2_ = { y: 0, v: 0, d: '' }

    let mouseX = svg ? e.pageX - svg.current.getBoundingClientRect().x : 0
    let day: number = Math.round(xAxisScale.invert(mouseX))

    if (dataC1) valC1 = (dataC1[drawProp][day] as unknown) as dataElement
    if (dataC2) valC2 = (dataC2[drawProp][day] as unknown) as dataElement

    if (valC1 && valC2) {
      move1 = valC1[0] > valC2[0] ? -50 : 8
      move2 = valC1[0] < valC2[0] ? -50 : 8
      if (valC1[0] < valC2[0]) isFirst = false
    }

    if (dataC1 && valC1) {
      tt1_ = {
        v: valC1[0],
        d: createDateString(parseDate(dataC1[drawProp + '_']), valC1[1]),
        y: getPos(valC1[0], move1, isFirst)
      }
    }
    if (dataC2 && valC2) {
      tt2_ = {
        v: valC2[0],
        d: createDateString(parseDate(dataC2[drawProp + '_']), valC2[1]),
        y: getPos(valC2[0], move2, !isFirst)
      }
    }

    setTtOne(tt1_)
    setTtTwo(tt2_)

    if (mouseX >= plotArea.left && mouseX <= plotArea.right) {
      setTtX(xAxisScale(day))
    }
  }

  return (
    <div className='chart'>
      <div ref={container} style={{ position: 'relative', width: '100%' }}>
        <svg
          ref={svg}
          width={width}
          height={height}
          style={{ backgroundColor: 'transparent' }}
          onMouseMove={config.isMobile ? undefined : handleMouseMove}
          onMouseLeave={() => {
            setTtX(80)
            setTtOne(undefined)
            setTtTwo(undefined)
          }}
        >
          <YTicks
            ticks={yTicks}
            x={[plotArea.left, plotArea.right]}
            scale={yAxisScale}
            textAnchor={config.isMobile ? 'start' : 'end'}
            textX={() => (config.isMobile ? 4 : ttX - 4)}
            textY={(t, ticks) =>
              yAxisScale(t) + (t !== ticks[ticks.length - 1] ? -4 : 14)
            }
            textT={(t, ticks) =>
              t !== ticks[0] ? f.format(Math.floor(t)) : ''
            }
          />
          <XTicks
            ticks={xTicks}
            y={[yAxisScale(yTicks[yTicks.length - 1]), plotArea.bottom]}
            scale={xAxisScale}
            textX={t => xAxisScale(t) + 2}
            textY={() => plotArea.bottom + 14}
            textT={t => f.format(Math.floor(t))}
          />

          {data
            .filter(f => f[drawProp].length > 0)
            .sort(a => {
              return hlCountries.indexOf(a.code) !== -1 ||
                selected.indexOf(a.code) !== -1
                ? 1
                : -1
            })
            .map((d, i, j) => {
              let hl = hlCountries.indexOf(d.code) === -1 ? false : true
              let sel = selected.indexOf(d.code) === -1 ? false : true
              let drawData: dataElement[] = d[drawProp]
              let lastValue: dataElement = drawData[drawData.length - 1]
              if (drawData.length < 1 || !lastValue) return null
              let labelX = xAxisScale(lastValue[1])
              let labelY = yAxisScale(lastValue[0]) + 5
              let offset = { x: 0, y: 0 }

              let selNr = selected.indexOf(d.code) === 0 ? 1 : 0

              if (sel) {
                offset = { x: 8, y: 4 }
                if (labelY < height * 0.2) offset.y += 10
                if (labelY > height * 0.8) offset.y -= 10
                if (labelX > width * 0.9) {
                  offset.x -= 24
                  if (labelY > height * 0.1 && labelY < height * 0.9)
                    offset.y -= 20
                }
                let otherLast
                if (dataC1 && dataC2) {
                  otherLast =
                    selNr === 0
                      ? dataC1[drawProp][dataC1[drawProp].length - 1] ||
                        undefined
                      : dataC2[drawProp][dataC2[drawProp].length - 1] ||
                        undefined
                }

                if (otherLast) {
                  let labelX2 = xAxisScale(otherLast[1])
                  let labelY2 = yAxisScale(otherLast[0])

                  if (
                    Math.abs(labelY2 - labelY) < 50 &&
                    Math.abs(labelX2 - labelX) < 80
                  ) {
                    labelY += otherLast[0] > lastValue[0] ? 15 : -15
                  }
                }
              }
              return (
                <g
                  className={
                    d.code +
                    (hl === true && sel == false ? ' hl ' : '') +
                    (sel === true
                      ? ' sel _' + (selected.indexOf(d.code) + 1)
                      : '')
                  }
                >
                  <path
                    // d={lineFunction(drawData).replace(/\.\d{1,}/g, '')}
                    d={lineFunction(drawData)}
                    fill='none'
                    className={'country ' + d.code}
                    onMouseEnter={e => {
                      setHlCountries([d.code])
                    }}
                    onMouseLeave={e => {
                      setHlCountries([])
                    }}
                    onClick={() => {
                      if (manualSelect) {
                        setHlCountries([])
                        manualSelect(d.code)
                      }
                    }}
                  />
                  {(hl === true || sel == true) && (
                    <>
                      <text
                        x={labelX + offset.x}
                        y={labelY + offset.y}
                        textAnchor={labelX > width * 0.9 ? 'end' : 'start'}
                        className='countryLabelBG'
                      >
                        {d.name}
                      </text>
                      <text
                        x={labelX + offset.x}
                        y={labelY + offset.y}
                        textAnchor={labelX > width * 0.9 ? 'end' : 'start'}
                        className='countryLabel'
                      >
                        {d.name}
                      </text>
                    </>
                  )}
                </g>
              )
            })}
          {!config.isMobile && (
            <g className='tt'>
              <line
                x1={ttX}
                x2={ttX}
                y1={plotArea.top}
                y2={plotArea.bottom}
                stroke='rgba(0,0,0,.4)'
                pointerEvents='none'
              />

              {ttOne && ttOne.v > 0 && (
                <>
                  <circle
                    cx={ttX}
                    cy={yAxisScale(ttOne.v)}
                    r={6}
                    className='ttCircle _1'
                  />
                  <g
                    className='tt1'
                    transform={
                      'translate(' +
                      (ttX <= 120 ? gutterLeft + 4 : ttX - 120) +
                      ',' +
                      ttOne.y +
                      ')'
                    }
                  >
                    <rect width='100' height='40' className='ttBG _1' />
                    <text x='50' y='18' className='ttText' textAnchor='middle'>
                      {popType === 'pop' && valueType === 'new'
                        ? f.format(ttOne.v)
                        : f.format(+ttOne.v.toFixed(1))}
                    </text>
                    <text
                      x='50'
                      y='34'
                      className='ttTextDate'
                      textAnchor='middle'
                    >
                      {ttOne.d}
                    </text>
                  </g>
                </>
              )}
              {ttTwo && ttTwo.v > 0 && (
                <>
                  <circle
                    cx={ttX}
                    cy={yAxisScale(ttTwo.v)}
                    r={6}
                    className='ttCircle _2'
                  />
                  <g
                    className='tt2'
                    transform={
                      'translate(' +
                      (ttX <= 120 ? gutterLeft + 4 : ttX - 120) +
                      ',' +
                      ttTwo.y +
                      ')'
                    }
                  >
                    <rect width='100' height='40' className='ttBG _2' />
                    <text x='50' y='18' className='ttText' textAnchor='middle'>
                      {popType === 'pop' && valueType === 'new'
                        ? f.format(ttTwo.v)
                        : f.format(+ttTwo.v.toFixed(1))}
                    </text>
                    <text
                      x='50'
                      y='34'
                      className='ttTextDate'
                      textAnchor='middle'
                    >
                      {ttTwo.d}
                    </text>
                  </g>
                </>
              )}
            </g>
          )}
        </svg>
      </div>
    </div>
  )
}
